import React from "react";
import { useState, useRef, useEffect } from "react";
import { Check, ChevronDown, X } from "lucide-react";
import { Badge } from "../../../components/ui/badge";
import { Button } from "../../../components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../../components/ui/popover";
import { cn } from "../../../lib/utils";
import { FieldDataOption } from "./Types";

interface MultiSelectFieldProps {
  options: FieldDataOption[];
  value: string[];
  onChange: (value: string[]) => void;
  placeholder?: string;
  disabled?: boolean;
}

export function MultiSelectField({
  options,
  value,
  onChange,
  placeholder = "Select options...",
  disabled = false,
}: MultiSelectFieldProps) {
  const [open, setOpen] = useState(false);

  const toggleOption = (optionCode: string) => {
    if (value.includes(optionCode)) {
      onChange(value.filter((v) => v !== optionCode));
    } else {
      onChange([...value, optionCode]);
    }
  };

  const removeOption = (optionCode: string, e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(value.filter((v) => v !== optionCode));
  };

  const selectedOptions = options.filter((opt) => value.includes(opt.code));

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            "w-full justify-between min-h-10 h-auto py-2",
            !value.length && "text-muted-foreground"
          )}
        >
          <div className="flex flex-wrap gap-1 flex-1">
            {selectedOptions.length > 0 ? (
              selectedOptions.map((opt) => (
                <Badge
                  key={opt.code}
                  variant="secondary"
                  className="mr-1 mb-1"
                >
                  {opt.description}
                  <button
                    className="ml-1 ring-offset-background rounded-full outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                    onClick={(e) => removeOption(opt.code, e)}
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              ))
            ) : (
              <span>{placeholder}</span>
            )}
          </div>
          <ChevronDown className="h-4 w-4 shrink-0 opacity-50 ml-2" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-full p-0 pointer-events-auto z-50" align="start">
        <div className="max-h-60 overflow-auto p-1">
          {options.length === 0 ? (
            <div className="py-6 text-center text-sm text-muted-foreground">
              No options available.
            </div>
          ) : (
            options.map((option, index) => (
              <div
                key={index}
                className={cn(
                  "flex items-center gap-2 px-2 py-1.5 cursor-pointer rounded-sm hover:bg-accent hover:text-accent-foreground",
                  value.includes(option.code) && "bg-accent"
                )}
                onClick={() => toggleOption(option.code)}
              >
                <div
                  className={cn(
                    "flex h-4 w-4 items-center justify-center rounded-sm border border-primary",
                    value.includes(option.code)
                      ? "bg-primary text-primary-foreground"
                      : "opacity-50"
                  )}
                  onClick={() => toggleOption(option.code)}
                >
                  {value.includes(option.code) && (
                    <Check className="h-3 w-3" />
                  )}
                </div>
                <span className="text-sm">{option.description} s</span>
              </div>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
